import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Injector,
    Input,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import {
    MatAutocomplete,
    MatAutocompleteActivatedEvent,
    MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { FormHelper } from '@klickdata/core/form';
import { Group, GroupService } from '@klickdata/core/group';
import { HttpErrorService } from '@klickdata/core/http';
import { MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, first, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-accounts-manager-chip-group-select',
    templateUrl: './accounts-manager-chip-group-select.component.html',
    styleUrls: ['./accounts-manager-chip-group-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AccountsManagerChipGroupSelectComponent),
            multi: true,
        },
    ],
})
export class AccountsManagerChipGroupSelectComponent implements OnInit {
    @ViewChild('groupInput') groupInput: ElementRef<HTMLInputElement>;
    @ViewChild('auto') matAutocomplete: MatAutocomplete;
    private destroy: Subject<boolean> = new Subject<boolean>();
    @Input() visible = true;
    @Input() selectable = true;
    @Input() removable = true;
    @Input() customer_id: number;
    @Input() placeholder = $localize`Add Group`;
    @Input() noGroupsAvailableMessage = $localize`No groups available`;
    @Input() set user_id(user_id: number) {
        if (user_id) {
            this.handleGroupIds(user_id);
        }
    }
    @Input() params: { [key: string]: any };
    separatorKeysCodes: number[] = [ENTER, COMMA];
    groupCtrl = new FormControl();
    filteredGroups: Observable<Group[]>;
    private _selection: Group[] = [];
    private _optionActivated: boolean;
    public hasAvailableGroups: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get selection(): Group[] {
        return this._selection;
    }
    set selection(value: Group[]) {
        this._selection = value;
        this.notify();
    }

    constructor(
        protected groupService: GroupService,
        protected cdRef: ChangeDetectorRef,
        protected config: ConfigService,
        protected auth: AuthService,
        protected errorService: HttpErrorService,
        protected injector: Injector,
        protected messageService: MessageService
    ) {}

    ngOnInit() {
        this.filteredGroups = combineLatest([
            this.groupCtrl.valueChanges.pipe(startWith('')),
            this.customer_id
                ? this.groupService.getCustomerGroupsById(this.customer_id, this.params)
                : this.groupService.getCustomerGroupsByParams(this.params),
        ]).pipe(
            filter(([term]) => typeof term === 'string'),
            tap(([_, source]) => {
                this.hasAvailableGroups.next(!!source?.length);
            }),
            map(([term, source]) => (term ? source.filter((group) => -1 !== group.name.indexOf(term)) : source)),
            takeUntil(this.destroy)
        );
    }

    /**
     * Not yet supported!
     * @param event from separators events.
     */
    add(event: MatChipInputEvent, source: Group[]): void {
        const input = event.input;
        const value = event.value?.trim();
        if (!value || this._optionActivated) {
            return;
        }
        // Add Group
        const suggested = source.find((item) => item.name.toLowerCase() === value.toLowerCase());
        if (suggested) {
            this.setGroupInput(input, suggested);
        } else {
            this.auth
                .getUser()
                .pipe(
                    first(),
                    // filter(user => user.role_value !== 'user'), // Groups are public and user can add groups
                    switchMap((user) =>
                        this.groupService.create({
                            name: value.trim(),
                            customer_id: user.customer_id,
                        })
                    ),
                    takeUntil(this.destroy),
                    catchError((err) => {
                        if (err && err.error && err.error.error) {
                            // this.messageService.openMessage(
                            //     MessageErrorComponent,
                            //     $localize`${value} already exists in another type, Go to admin settings to attach.`
                            // );
                            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
                        }
                        return of(null);
                    })
                )
                .subscribe((group) => this.setGroupInput(input, group));
        }
    }

    private setGroupInput(input: HTMLInputElement, group: Group) {
        this.addGroup(group);
        // Reset the input value
        if (input) {
            input.value = '';
        }
        this.groupCtrl.setValue(null);
    }

    remove(group: Group): void {
        const index = this.selection.findIndex((removedGroup) => removedGroup?.name === group.name);
        if (index !== -1) {
            this.selection.splice(index, 1);
            this.notify();
            this.groupCtrl.enable();
        }
    }

    onSelected(event: MatAutocompleteSelectedEvent): void {
        const selected = event.option.value;
        this.addGroup(selected);
    }

    optionActivated(event: MatAutocompleteActivatedEvent) {
        this._optionActivated = !!event.option;
    }

    optionClosed() {
        this._optionActivated = false;
    }

    private addGroup(selected: any) {
        // Duplication not aceptable.
        if (this.selection.findIndex((group) => group?.id === selected.id) === -1) {
            this.selection.push(selected);
            this.notify();
            this.groupInput.nativeElement.value = '';
            this.groupCtrl.setValue(null);
            this.cdRef.markForCheck();
        }
    }

    public propagateChange = (_: any) => {};
    writeValue(ids: any): void {
        if (!ids?.length || ids === null) {
            this.selection.length = 0;
            this.groupCtrl.reset();
        }
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }
    registerOnTouched(fn: any): void {}
    setDisabledState?(isDisabled: boolean): void {}

    private notify() {
        this.propagateChange(this.selection.map((group) => group.id));
    }

    private handleGroupIds(id: number) {
        this.groupService
            .getGroupAdminGroups(id, this.params)
            .pipe(takeUntil(this.destroy))
            .subscribe((groups) => this.initSelection(groups));
    }

    private initSelection(value: Group[]) {
        this.selection = value;
        this.cdRef.markForCheck();
        this.resetFormControl();
    }

    private resetFormControl() {
        const control = this.injector.get(NgControl)?.control;
        if (control instanceof FormControl) {
            FormHelper.resetForm(control);
        }
    }
}
